import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Clock, Award, Star, Phone, CheckCircle, Wrench, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const reasons = [
  { icon: Award, text: "Experienced licensed plumbers" },
  { icon: Users, text: "Led by owner Fischer McKenzie" },
  { icon: Shield, text: "Alabama License MP-4174" },
  { icon: CheckCircle, text: "Fully licensed and insured" },
  { icon: Star, text: "5-star rated by our customers" },
  { icon: Wrench, text: "Free estimates on all projects" },
  { icon: Clock, text: "24/7 emergency service available" },
];

const trustBadges = [
  "Licensed (MP-4174)",
  "Insured",
  "Licensed & Experienced",
  "5-Star Rated",
  "Free Estimates",
  "24/7 Emergency",
  "Owner-Operated",
];

export const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 bg-background relative" id="why-choose-us">
      <div className="container mx-auto px-4" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-primary font-semibold tracking-wider uppercase text-sm">
            Why Us
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Why Homeowners Choose Onyx
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Fast response, honest pricing, and work done right the first time — that's why Baldwin County families keep calling us back.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Reasons List */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <div className="space-y-4">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.text}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.05 }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border/50"
                >
                  <reason.icon className="h-6 w-6 text-primary shrink-0" />
                  <span className="font-medium text-foreground">{reason.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl border border-border p-8 md:p-10"
          >
            <h3 className="text-2xl font-bold mb-4 text-foreground">
              Ready to Get Started?
            </h3>
            <p className="text-muted-foreground mb-6">
              Same-day service available. Free estimates on every job, no overtime charges — just honest, reliable plumbing.
            </p>
            <Button variant="hero" size="xl" className="w-full mb-6" asChild>
              <a href="tel:251-504-4452">
                <Phone className="h-5 w-5" />
                Call (251) 504-4452
              </a>
            </Button>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Available 24/7 • License MP-4174 • Free Estimates
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 justify-center">
              {trustBadges.map((badge) => (
                <span
                  key={badge}
                  className="bg-primary/10 text-primary text-xs font-medium px-3 py-1.5 rounded-full border border-primary/20"
                >
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
